import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";

import { SnackbarProvider } from "notistack";
import Routes from "routes/route";
import WhatsAppFloat from "components/Whatsapp/WhatsAppFloat";
import store from "store/store";
import { MaterialDesignContent } from "notistack";
import styled from "styled-components";
import { SnackbarConfigurator } from "./Notify";
import { ModalProvider } from "utils/context";
import { ModalProviderT } from "Context/GlobalModalContext";
import GlobalModal from "Context/GlobalModal";
import ErrorBoundary from "components/ErrorBoundary";
import { logFrontendError } from "utils/errorLogger";

// Slick carousel global styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { HelmetProvider } from "react-helmet-async";
import { EcommerceProvider } from "Context/UserSideContext";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { ENABLE_OFFLINE_POS } from "constants/appConstants";
import { startBuildRefresh } from "utils/buildRefresh";
import ConnectivityBanner from "components/ConnectivityBanner";

import theme from "styles/theme/theme";
const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  "&.notistack-MuiContent-success": {
    backgroundColor: "#dd4498",
  },
  "&.notistack-MuiContent-error": {
    backgroundColor: "#688ad5",
  },
  "&.notistack-MuiContent-info": {
    backgroundColor: "#cd1479",
  },
}));
const APP_VERSION = "1.0";

const storedVersion = localStorage.getItem("app_version");

if (storedVersion !== APP_VERSION) {

  const keysToKeep = ["app_version", "access-token", "authToken", "refresh-token", "cart", "transaction_id", "ischecklistopened", "OrderlistTXID"]; // keys you want to keep

  Object.keys(localStorage).forEach((key) => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  sessionStorage.clear();

  localStorage.setItem("app_version", APP_VERSION);
}

const DASHBOARD_PATH = /^\/(dashboard|website|store|reports|marketing|account)/;

const WhatsAppFloatGuard = () => {
  const { pathname } = useLocation();
  return DASHBOARD_PATH.test(pathname) ? null : <WhatsAppFloat />;
};

if ("serviceWorker" in navigator && process.env.NODE_ENV === "production") {
  window.addEventListener("load", () => {
    if (ENABLE_OFFLINE_POS) {
      // Named service-worker.js (not offline-service-worker.js) to match an
      // existing nginx rule — `location = /service-worker.js` — that serves
      // this exact filename with Cache-Control: no-store. Renaming this file
      // as offline-service-worker.js meant that rule never matched, so it fell
      // through to the aggressive 1-year immutable caching applied to every
      // other .js file, which is what let the whole v3/v4 cache-poisoning
      // incident go unnoticed by browsers for so long. Keep the `?v=` bump on
      // every CACHE_NAME change anyway as a second safety net.
      navigator.serviceWorker
        .register(`${process.env.PUBLIC_URL}/service-worker.js?v=5`)
        .catch(() => undefined);

      // Ask the browser to check for a newer SW script whenever the tablet
      // resumes — cheap, and doesn't itself activate anything (no skipWaiting
      // here), it just keeps the "waiting" worker fresh for buildRefresh.js.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          navigator.serviceWorker.ready
            .then(registration => registration.update())
            .catch(() => undefined);
        }
      });
    } else {
      navigator.serviceWorker
        .getRegistrations()
        .then(regs => regs.forEach(r => r.unregister()))
        .catch(() => undefined);
    }
  });
}

startBuildRefresh();

window.addEventListener("error", (event) => {
  logFrontendError({
    message: event.message,
    stack: event.error?.stack,
    source: "window-error",
    severity: "error",
  });
});

window.addEventListener("unhandledrejection", (event) => {
  logFrontendError({
    message: event.reason?.message ?? String(event.reason),
    stack: event.reason?.stack,
    source: "unhandled-rejection",
    severity: "error",
  });
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HelmetProvider>
          <Provider store={store}>
            <EcommerceProvider>
              <SnackbarProvider
                Components={{
                  success: StyledMaterialDesignContent,
                  error: StyledMaterialDesignContent,
                  info: StyledMaterialDesignContent,
                }}
                autoHideDuration={3000}
                maxSnack={3}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <BrowserRouter>
                <ConnectivityBanner />
                  <SnackbarConfigurator />
                  <ModalProviderT>
                    <ModalProvider>
                      <Routes />
                      <GlobalModal />
                    </ModalProvider>
                  </ModalProviderT>
                  <WhatsAppFloatGuard />
                </BrowserRouter>
              </SnackbarProvider>
            </EcommerceProvider>
          </Provider>
        </HelmetProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);